import { useMemo, useState, type MouseEvent } from 'react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  TruncateText,
} from '@mint-lib/tw-components';
import {
  ComboboxInput,
  ComboboxItem,
  ComboboxMenu,
} from '@mint-lib/tw-components/patterns';

import type { ColumnOption } from '../../../api/queries/userColumnsConfig.js';

type Props = {
  options: ColumnOption[];
  value?: string | number;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (newRole: string | number) => void;
  // When the dropdown is rendered inside a Radix Dialog, body-level portals
  // get caught by the dialog's scroll lock so two-finger scroll on the menu
  // doesn't reach the cmdk list. Pass `isPortal={false}` + a container ref
  // inside the dialog to keep the popover within the dialog's DOM tree.
  isPortal?: boolean;
  container?: HTMLElement | null;
};

const RoleSelect = ({
  options,
  value,
  placeholder = '',
  disabled,
  onChange,
  isPortal = true,
  container,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);

  const labelToValue = useMemo(
    () => new Map(options.map((opt) => [opt.label, opt.value])),
    [options],
  );

  const valueToLabel = useMemo(
    () => new Map(options.map((opt) => [String(opt.value), opt.label])),
    [options],
  );

  const currentLabel =
    valueToLabel.get(String(value ?? '')) ?? String(value ?? '');

  const handleSelect = (selectedLabel: string) => {
    const next = labelToValue.get(selectedLabel);
    if (next === undefined) return;
    if (String(next) === String(value ?? '')) return;
    setOpen(false);
    onChange?.(next);
  };

  // Only show the tooltip when the role text is actually clipped.
  const handleMouseEnter = (event: MouseEvent<HTMLDivElement>) => {
    const input = event.currentTarget.querySelector('input');
    if (input) {
      setIsTruncated(input.scrollWidth > input.clientWidth);
    }
  };

  return (
    <Tooltip
      open={tooltipOpen && isTruncated && !open}
      onOpenChange={setTooltipOpen}
    >
      <TooltipTrigger asChild>
        <div className="w-full min-w-0" onMouseEnter={handleMouseEnter}>
          <ComboboxInput
            value={currentLabel}
            open={open}
            onOpenChange={setOpen}
            size="small"
            isPortal={isPortal}
            container={container ?? undefined}
            disabled={disabled}
            placeholder={placeholder}
            popoverClassName="z-popover"
            inputProps={{ className: 'truncate' }}
          >
            <ComboboxMenu withSearch>
              {options.map((opt) => (
                <ComboboxItem
                  key={opt.label}
                  value={opt.label}
                  selected={opt.label === currentLabel}
                  onSelect={handleSelect}
                >
                  <TruncateText>
                    <span className="block">{opt.label}</span>
                  </TruncateText>
                </ComboboxItem>
              ))}
            </ComboboxMenu>
          </ComboboxInput>
        </div>
      </TooltipTrigger>
      <TooltipContent
        align="center"
        side="top"
        sideOffset={8}
        className="z-tooltip max-w-[320px] break-words"
      >
        {currentLabel}
      </TooltipContent>
    </Tooltip>
  );
};

export default RoleSelect;
